import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  Activity, 
  CheckCircle2, 
  AlertCircle, 
  XCircle,
  RefreshCw,
  Server,
  Database,
  Mic,
  Brain,
  Wifi
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { SUPABASE_FUNCTIONS_URL } from '@/integrations/supabase/config';

interface ServiceStatus {
  name: string;
  status: 'operational' | 'degraded' | 'outage' | 'checking';
  latency?: number;
  lastChecked: Date;
  icon: React.ReactNode;
}

interface SystemStatusIndicatorProps {
  compact?: boolean;
  showDetails?: boolean;
}

const SystemStatusIndicator: React.FC<SystemStatusIndicatorProps> = ({
  compact = true,
  showDetails = true
}) => {
  const [services, setServices] = useState<ServiceStatus[]>([
    { name: 'API Principal', status: 'checking', lastChecked: new Date(), icon: <Server className="h-4 w-4" /> },
    { name: 'Banco de Dados', status: 'checking', lastChecked: new Date(), icon: <Database className="h-4 w-4" /> },
    { name: 'ConsultorIA', status: 'checking', lastChecked: new Date(), icon: <Brain className="h-4 w-4" /> },
    { name: 'Voz', status: 'checking', lastChecked: new Date(), icon: <Mic className="h-4 w-4" /> },
  ]);
  const [isChecking, setIsChecking] = useState(false);
  const [lastFullCheck, setLastFullCheck] = useState<Date | null>(null);

  const checkServices = async () => {
    setIsChecking(true);
    const updatedServices: ServiceStatus[] = [];

    // Get session
    let userId: string | undefined;
    let hasSession = false;
    try {
      const { data } = await supabase.auth.getSession();
      userId = data.session?.user?.id;
      hasSession = Boolean(data.session);
    } catch {
      // ignore
    }

    // Check Supabase API
    const apiStart = Date.now();
    try {
      const { error } = await supabase.from('profiles').select('id').limit(1);
      const latency = Date.now() - apiStart;
      updatedServices.push({
        name: 'API Principal',
        status: error ? 'degraded' : 'operational',
        latency,
        lastChecked: new Date(),
        icon: <Server className="h-4 w-4" />
      });
    } catch {
      updatedServices.push({
        name: 'API Principal',
        status: 'outage',
        lastChecked: new Date(),
        icon: <Server className="h-4 w-4" />
      });
    }

    // Check Database via simple query (no auth required)
    const dbStart = Date.now();
    try {
      const { error } = await supabase.from('product_categories').select('id').limit(1);
      const latency = Date.now() - dbStart;
      updatedServices.push({
        name: 'Banco de Dados',
        status: error ? 'degraded' : 'operational',
        latency,
        lastChecked: new Date(),
        icon: <Database className="h-4 w-4" />
      });
    } catch {
      updatedServices.push({
        name: 'Banco de Dados',
        status: 'outage',
        lastChecked: new Date(),
        icon: <Database className="h-4 w-4" />
      });
    }

    // Check AI Service - only if logged in (requires auth)
    const aiStart = Date.now();
    if (hasSession) {
      try {
        type ProviderStatus = {
          provider: string;
          status: 'ok' | 'error' | 'not_configured';
        };

        type TestLLMProvidersResponse = {
          success: boolean;
          providers: ProviderStatus[];
        };

        const { data, error } = await supabase.functions.invoke<TestLLMProvidersResponse>('test-llm-providers', {
          body: {},
        });

        const latency = Date.now() - aiStart;
        const geminiOk = Boolean(
          data?.providers?.some((p) => p.provider === 'Google Gemini' && p.status === 'ok')
        );

        updatedServices.push({
          name: 'ConsultorIA',
          status: !error && geminiOk ? 'operational' : 'degraded',
          latency,
          lastChecked: new Date(),
          icon: <Brain className="h-4 w-4" />
        });
      } catch {
        updatedServices.push({
          name: 'ConsultorIA',
          status: 'degraded',
          lastChecked: new Date(),
          icon: <Brain className="h-4 w-4" />
        });
      }
    } else {
      // Not logged in - assume operational (can't test without auth)
      updatedServices.push({
        name: 'ConsultorIA',
        status: 'operational',
        lastChecked: new Date(),
        icon: <Brain className="h-4 w-4" />
      });
    }

    // Check Voice Service (public health endpoint - no auth needed)
    const voiceStart = Date.now();
    try {
      const response = await fetch(
        `${SUPABASE_FUNCTIONS_URL}/elevenlabs-tts`,
        { method: 'GET' }
      );

      const latency = Date.now() - voiceStart;
      const payload = (await response.json().catch(() => null)) as null | { ok?: boolean; keyConfigured?: boolean };
      const isUp = response.ok && payload?.ok === true && payload?.keyConfigured === true;

      updatedServices.push({
        name: 'Voz',
        status: isUp ? 'operational' : 'degraded',
        latency,
        lastChecked: new Date(),
        icon: <Mic className="h-4 w-4" />
      });
    } catch {
      updatedServices.push({
        name: 'Voz',
        status: 'degraded',
        lastChecked: new Date(),
        icon: <Mic className="h-4 w-4" />
      });
    }

    setServices(updatedServices);
    setLastFullCheck(new Date());
    setIsChecking(false);
  };

  useEffect(() => {
    checkServices();
    // Check every 5 minutes
    const interval = setInterval(checkServices, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const getOverallStatus = () => {
    if (services.some(s => s.status === 'checking')) return 'checking';
    if (services.some(s => s.status === 'outage')) return 'outage';
    if (services.some(s => s.status === 'degraded')) return 'degraded';
    return 'operational';
  };

  const getStatusIcon = (status: ServiceStatus['status']) => {
    switch (status) {
      case 'operational':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'degraded':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'outage':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'checking':
        return <RefreshCw className="h-4 w-4 text-muted-foreground animate-spin" />;
    }
  };

  const getStatusLabel = (status: ServiceStatus['status']) => {
    switch (status) {
      case 'operational': return 'Operacional';
      case 'degraded': return 'Degradado';
      case 'outage': return 'Indisponível';
      case 'checking': return 'Verificando...';
    }
  };

  const getStatusColor = (status: ServiceStatus['status']) => {
    switch (status) {
      case 'operational': return 'bg-green-500';
      case 'degraded': return 'bg-yellow-500';
      case 'outage': return 'bg-red-500';
      case 'checking': return 'bg-muted-foreground animate-pulse';
    }
  };

  const overallStatus = getOverallStatus();

  if (compact) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            className="gap-2 h-8"
          >
            <div className={`h-2 w-2 rounded-full ${getStatusColor(overallStatus)}`} />
            <span className="text-xs text-muted-foreground hidden sm:inline">
              {overallStatus === 'operational' ? 'Sistemas OK' : 
               overallStatus === 'checking' ? 'Verificando...' :
               'Problemas detectados'}
            </span>
            <Activity className="h-3 w-3 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        
        {showDetails && (
          <PopoverContent className="w-80" align="end">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-sm flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  Status do Sistema
                </h4>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={checkServices}
                  disabled={isChecking}
                >
                  <RefreshCw className={`h-3 w-3 ${isChecking ? 'animate-spin' : ''}`} />
                </Button>
              </div>

              <div className="space-y-2">
                {services.map((service) => (
                  <div 
                    key={service.name}
                    className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/50"
                  >
                    <div className="flex items-center gap-2">
                      {service.icon}
                      <span className="text-sm">{service.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {service.latency && (
                        <span className="text-xs text-muted-foreground">
                          {service.latency}ms
                        </span>
                      )}
                      {getStatusIcon(service.status)}
                    </div>
                  </div>
                ))}
              </div>

              {lastFullCheck && (
                <p className="text-xs text-muted-foreground text-center">
                  Última verificação: {lastFullCheck.toLocaleTimeString('pt-BR')}
                </p>
              )}
            </div>
          </PopoverContent>
        )}
      </Popover>
    );
  }

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Status do Sistema
          </h4>
          <div className="flex items-center gap-2">
            <Badge 
              variant="secondary"
              className={
                overallStatus === 'operational' ? 'bg-green-100 text-green-700' :
                overallStatus === 'degraded' ? 'bg-yellow-100 text-yellow-700' :
                overallStatus === 'outage' ? 'bg-red-100 text-red-700' :
                'bg-muted'
              }
            >
              {getStatusLabel(overallStatus)}
            </Badge>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={checkServices}
              disabled={isChecking}
            >
              <RefreshCw className={`h-4 w-4 ${isChecking ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {services.map((service) => (
            <div 
              key={service.name}
              className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/50"
            >
              <div className="flex items-center gap-2">
                {service.icon}
                <span className="text-sm">{service.name}</span>
              </div>
              {getStatusIcon(service.status)}
            </div>
          ))}
        </div>

        {lastFullCheck && (
          <p className="text-xs text-muted-foreground text-center mt-3">
            Atualizado: {lastFullCheck.toLocaleTimeString('pt-BR')}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default SystemStatusIndicator;
